import React, { useState, useEffect } from "react";
import { getDriverPayoutById, updateDriverPayout } from "../../api/DriverPayoutApi.js";
import { getAllDrivers } from "../../api/driverApi.js";
import { getAllVehicleClientAssignments } from "../../api/vehicalsManagementApi.js";
import { getAllDriverPaySlabs } from "../../api/DriverPaySlabApi.js";
import { X, Save } from "lucide-react";
import CustomSelect from "../shared/CustomSelect";
import { toast } from "../shared/Toast.jsx";

const formatDateForInput = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("T")) {
    return dateStr.split("T")[0];
  }
  return dateStr;
};

const EditDriverPayoutModal = ({ id, close, refresh }) => {
  const [drivers, setDrivers] = useState([]);
  const [slabs, setSlabs] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const [form, setForm] = useState({
    driverId: "",
    fromDate: "",
    toDate: "",
    totalTrips: "",
    totalAmount: "",
    fuelDeduction: 0,
    penaltyDeduction: 0,
    advanceDeduction: 0,
    otherDeduction: 0,
    status: "Pending",
    remarks: ""
  });

  useEffect(() => {
    const initData = async () => {
      try {
        setFetching(true);
        // Load supporting lists first
        const [driversRes, slabsRes, assignmentsRes, payoutRes] = await Promise.all([
          getAllDrivers(),
          getAllDriverPaySlabs(),
          getAllVehicleClientAssignments(),
          getDriverPayoutById(id)
        ]);

        setDrivers(driversRes.data || []);
        if (slabsRes.success) setSlabs(slabsRes.data || []);
        if (assignmentsRes.success) setAssignments(assignmentsRes.data || []);

        if (payoutRes.success && payoutRes.data) {
          const p = payoutRes.data;
          setForm({
            driverId: p.driverId || p.driver?.id || "",
            fromDate: formatDateForInput(p.fromDate),
            toDate: formatDateForInput(p.toDate),
            totalTrips: p.totalTrips || "",
            totalAmount: p.totalAmount || "",
            fuelDeduction: p.fuelDeduction || 0,
            penaltyDeduction: p.penaltyDeduction || 0,
            advanceDeduction: p.advanceDeduction || 0,
            otherDeduction: p.otherDeduction || 0,
            status: p.status || "Pending",
            remarks: p.remarks || ""
          });
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to load payout details");
      } finally {
        setFetching(false);
      }
    };
    initData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newForm = {
      ...form,
      [name]: value
    };

    if (name === "driverId" || name === "totalTrips") {
      const driverId = name === "driverId" ? value : form.driverId;
      const totalTrips = name === "totalTrips" ? Number(value) : Number(form.totalTrips);
      
      const driverObj = drivers.find(d => String(d.id) === String(driverId));
      if (driverObj && driverObj.vehicleId) {
        const assignment = assignments.find(a => String(a.vehicleId) === String(driverObj.vehicleId));
        if (assignment) {
          const clientId = assignment.clientId;
          const slab = slabs.find(s => String(s.vehicleId) === String(driverObj.vehicleId) && String(s.clientId) === String(clientId));
          if (slab) {
            const calculatedAmount = (slab.rate || 0) * (totalTrips || 0);
            newForm.totalAmount = calculatedAmount;
          }
        }
      }
    }

    setForm(newForm);
  };

  const totalDeduction =
    Number(form.fuelDeduction || 0) +
    Number(form.penaltyDeduction || 0) +
    Number(form.advanceDeduction || 0) +
    Number(form.otherDeduction || 0);

  const payableAmount = Number(form.totalAmount || 0) - totalDeduction;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.driverId || !form.fromDate || !form.toDate) {
      toast.warning("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        driverId: form.driverId,
        fromDate: form.fromDate,
        toDate: form.toDate,
        totalTrips: Number(form.totalTrips),
        totalAmount: Number(form.totalAmount),
        fuelDeduction: Number(form.fuelDeduction),
        penaltyDeduction: Number(form.penaltyDeduction),
        advanceDeduction: Number(form.advanceDeduction),
        otherDeduction: Number(form.otherDeduction),
        totalDeduction,
        payableAmount,
        status: form.status,
        remarks: form.remarks
      };

      await updateDriverPayout(id, payload);
      toast.success("Driver payout updated successfully");
      refresh();
      close();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update payout");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div style={backdropStyle}>
        <div style={{ ...modalStyle, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
          <div style={{ color: "#20B967", fontWeight: 600 }}>Loading details...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={backdropStyle}>
      <form onSubmit={handleSubmit} style={modalStyle}>
        <div style={headerStyle}>
          <h2 style={{ margin: 0, color: "#fff", fontSize: "20px", fontWeight: 600 }}>Edit Driver Payout</h2>
          <button type="button" onClick={close} style={closeBtnStyle}><X size={20} /></button>
        </div>

        <div style={bodyStyle}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div>
              <label style={labelStyle}>Driver *</label>
              <CustomSelect
                searchable
                options={drivers.map(d => ({
                  value: d.id,
                  label: `${d.driverName} ${d.vehicle?.vehicleNumber ? `(${d.vehicle.vehicleNumber})` : ''}`
                }))}
                value={form.driverId}
                onChange={(val) => handleChange({ target: { name: 'driverId', value: val } })}
                placeholder="Select Driver"
              />
            </div>

            <div>
              <label style={labelStyle}>Status</label>
              <CustomSelect
                options={["Pending", "Paid", "Hold"]}
                value={form.status}
                onChange={(val) => handleChange({ target: { name: 'status', value: val } })}
                placeholder="Select Status"
              />
            </div>

            <div>
              <label style={labelStyle}>From Date *</label>
              <input
                type="date"
                name="fromDate"
                value={form.fromDate}
                onChange={handleChange}
                required
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>To Date *</label>
              <input
                type="date"
                name="toDate"
                value={form.toDate}
                onChange={handleChange}
                required
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Total Trips</label>
              <input
                type="number"
                name="totalTrips"
                value={form.totalTrips}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Total Amount (₹)</label>
              <input
                type="number"
                name="totalAmount"
                value={form.totalAmount}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Fuel Deduction (₹)</label>
              <input
                type="number"
                name="fuelDeduction"
                value={form.fuelDeduction}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Penalty Deduction (₹)</label>
              <input
                type="number"
                name="penaltyDeduction"
                value={form.penaltyDeduction}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Advance Deduction (₹)</label>
              <input
                type="number"
                name="advanceDeduction"
                value={form.advanceDeduction}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Other Deduction (₹)</label>
              <input
                type="number"
                name="otherDeduction"
                value={form.otherDeduction}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div style={{ gridColumn: "span 2", padding: "12px", background: "#FEF2F2", borderRadius: "8px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", fontWeight: 600, color: "#991B1B" }}>
                <span>Total Deductions:</span>
                <span>₹ {totalDeduction}</span>
              </div>
            </div>

            <div style={{ gridColumn: "span 2", padding: "12px", background: "#ECFDF5", borderRadius: "8px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "16px", fontWeight: 700, color: "#065F46" }}>
                <span>Final Payable Amount:</span>
                <span>₹ {payableAmount}</span>
              </div>
            </div>

            <div style={{ gridColumn: "span 2" }}>
              <label style={labelStyle}>Remarks</label>
              <textarea
                name="remarks"
                value={form.remarks}
                onChange={handleChange}
                style={{ ...inputStyle, height: "60px", resize: "none" }}
              />
            </div>
          </div>
        </div>

        <div style={footerStyle}>
          <button type="button" onClick={close} style={cancelBtnStyle}>Cancel</button>
          <button type="submit" disabled={loading} style={submitBtnStyle}>
            <Save size={16} style={{ marginRight: "6px" }} />
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

const backdropStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000
};

const modalStyle = {
  background: "#fff",
  width: "600px",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
  display: "flex",
  flexDirection: "column",
  maxHeight: "90vh"
};

const headerStyle = {
  backgroundColor: "#212A30",
  padding: "16px 20px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
};

const closeBtnStyle = {
  background: "none",
  border: "none",
  color: "#fff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center"
};

const bodyStyle = {
  padding: "20px",
  overflowY: "auto"
};

const labelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: 500,
  color: "#374151",
  marginBottom: "6px"
};

const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  border: "1px solid #E5E7EB",
  borderRadius: "8px",
  fontSize: "14px",
  boxSizing: "border-box",
  outline: "none"
};

const footerStyle = {
  padding: "12px 20px",
  borderTop: "1px solid #F3F4F6",
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
  backgroundColor: "#F9FAFB"
};

const cancelBtnStyle = {
  backgroundColor: "#fff",
  color: "#374151",
  border: "1px solid #D1D5DB",
  padding: "8px 16px",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: 500
};

const submitBtnStyle = {
  backgroundColor: "#20B967",
  color: "#fff",
  border: "none",
  padding: "8px 16px",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: 600,
  display: "flex",
  alignItems: "center"
};

export default EditDriverPayoutModal;
